import { useMemo } from 'react';
import { NO_DELIVERABLE_LABEL } from '../constants/messages.js';
import '../styles/deliverable-set.css';

/**
 * 進捗管理詳細の成果物セット編集ウィジェット。
 * 1成果物 = 3行（成果物名 / 成果物URL / チェックボックス2つ）のまとまりを
 * 縦に並べ、行の追加・削除で複数成果物を扱う。
 *
 * 成果物が無い進捗管理には「成果物なし」を付ける。これは成果物名「なし」・URL なしの
 * 印で、他の成果物とは併存できない。
 *
 * value: [{ id, product, deliverable_url, is_deliverable_stored, pmo_deliverable_check }, ...]
 * options: 同一小項目で選択可能な成果物候補 [{ id, product, deliverable_url }, ...]
 */
export default function DeliverableSetEditor({ value, onChange, options = [], readOnly = false }) {
  const items = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  // このウィジェットは進捗管理1件（＝1つの領域）の成果物セットを扱う。
  // 同じ領域の中では「成果物なし」と通常の成果物は併存できない。
  const hasNoDeliverableMark = items.some((item) => item.no_deliverable);
  const hasRealDeliverable = items.some(
    (item) => !item.no_deliverable && (item.product || item.deliverable_url || item.id),
  );

  // 成果物名 → 候補（同名は小項目まで絞れば一意なので name で解決できる）
  const optionByProduct = useMemo(() => {
    const map = new Map();
    options.forEach((opt) => {
      // 「成果物なし」はボタンで付け外しするので、名前の候補には出さない
      if (opt.no_deliverable) return;
      if (opt.product && !map.has(opt.product)) map.set(opt.product, opt);
    });
    return map;
  }, [options]);

  const updateItem = (index, patch) => {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange(next);
  };

  // 別の成果物に切り替えたときは、前の成果物のチェックを引き継がない
  const CLEARED_CHECKS = { is_deliverable_stored: false, pmo_deliverable_check: false };

  // 選択解除する前に紐付いていた成果物の ID。
  // 同じ成果物に戻したときだけチェックを残すために覚えておく。
  const previousIdOf = (item) => item.id ?? item.previousId ?? null;

  // ドロップダウンから既存成果物を選択
  const handleSelectProduct = (index, product) => {
    const item = items[index];
    if (!product) {
      // 「（既存から選択）」に戻した = 選択解除。
      // 同じ成果物に戻す可能性があるので、チェックはまだ消さない。
      updateItem(index, {
        product: '',
        id: null,
        deliverable_url: '',
        previousId: previousIdOf(item),
      });
      return;
    }
    const matched = optionByProduct.get(product);
    if (!matched) {
      // 候補一覧に無い現在値（別範囲の既存リンクなど）を選び直した場合はそのまま維持
      updateItem(index, { product });
      return;
    }
    const isSameDeliverable = matched.id === previousIdOf(item);
    updateItem(index, {
      product,
      id: matched.id,
      deliverable_url: matched.deliverable_url || '',
      previousId: null,
      ...(isSameDeliverable ? {} : CLEARED_CHECKS),
    });
  };

  // テキストで新規入力（候補に無い成果物を作成）
  const handleNewProduct = (index, product) => {
    const item = items[index];
    // 既存成果物から新規入力に切り替えたときもチェックは引き継がない
    const wasExisting = previousIdOf(item) !== null;
    updateItem(index, {
      product,
      id: null,
      previousId: null,
      ...(wasExisting ? CLEARED_CHECKS : {}),
    });
  };

  const addItem = () => {
    onChange([
      ...items,
      {
        id: null,
        product: '',
        deliverable_url: '',
        no_deliverable: false,
        is_deliverable_stored: false,
        pmo_deliverable_check: false,
      },
    ]);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  // 「成果物なし」を付ける／外す。他の成果物と併存できるので既存のセットに足す。
  // 同じ座標に「なし」が既にあればそれを選択する（新規入力は同名エラーになる）。
  // 座標の決まっていない「なし」は別座標の残骸なので再利用しない。拾ってしまうと
  // この行の座標で登録されず、成果物管理にも新しい行が増えない。
  const toggleNoDeliverable = () => {
    if (hasNoDeliverableMark) {
      onChange(items.filter((item) => !item.no_deliverable));
      return;
    }
    const existing = options.find(
      (option) => option.no_deliverable && !option.unlinked,
    );
    onChange([...items, {
      id: existing ? existing.id : null,
      product: NO_DELIVERABLE_LABEL,
      no_deliverable: true,
      deliverable_url: '',
      is_deliverable_stored: false,
      pmo_deliverable_check: false,
    }]);
  };

  const productOptions = useMemo(
    () => ['', ...Array.from(optionByProduct.keys())],
    [optionByProduct],
  );

  if (readOnly) {
    if (items.length === 0) return <span style={{ color: '#9ca3af' }}>—</span>;
    return (
      <div className="deliverable-set">
        {items.map((item, i) => (
          <div className="deliverable-set__card" key={`dlv-ro-${item.id ?? i}`}>
            <div className="deliverable-set__field">成果物名：{item.product || '—'}</div>
            <div className="deliverable-set__field">
              成果物URL：
              {item.deliverable_url ? (
                <a href={item.deliverable_url} target="_blank" rel="noopener noreferrer">
                  {item.deliverable_url}
                </a>
              ) : '—'}
            </div>
            <div className="deliverable-set__checks">
              <span>{item.is_deliverable_stored ? '☑' : '☐'} 成果物保管済み</span>
              <span>{item.pmo_deliverable_check ? '☑' : '☐'} PMO成果物保管チェック</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="deliverable-set">
      {items.map((item, i) => {
        // 「成果物なし」は名前を編集させない。URL は任意項目なので入力できる。
        if (item.no_deliverable) {
          return (
            <div className="deliverable-set__card" key={`dlv-${i}`}>
              <div className="deliverable-set__field">成果物なし</div>
              <div className="deliverable-set__field">
                <label className="deliverable-set__label">成果物URL（任意）</label>
                <input
                  className="db-input-control"
                  type="url"
                  value={item.deliverable_url || ''}
                  onChange={(e) => updateItem(i, { deliverable_url: e.target.value })}
                  placeholder="https://..."
                  readOnly={Boolean(item.id)}
                  title={item.id
                    ? '既存成果物のURLは成果物管理画面で編集してください'
                    : undefined}
                />
              </div>
              <div className="deliverable-set__checks">
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(item.is_deliverable_stored)}
                    onChange={(e) => updateItem(i, { is_deliverable_stored: e.target.checked })}
                  />
                  成果物保管済み
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(item.pmo_deliverable_check)}
                    onChange={(e) => updateItem(i, { pmo_deliverable_check: e.target.checked })}
                  />
                  PMO成果物保管チェック
                </label>
                <button
                  type="button"
                  className="deliverable-set__remove"
                  onClick={() => removeItem(i)}
                  title="「成果物なし」を外す"
                >
                  ×
                </button>
              </div>
            </div>
          );
        }
        // 既存成果物を選択中か（id ありかつ名前が確定）
        const isExisting = Boolean(item.id);
        // ドロップダウン候補に現在値（別範囲の既存リンク等）が無ければ加える
        const selectOpts = item.product && !optionByProduct.has(item.product)
          ? [...productOptions, item.product]
          : productOptions;
        return (
          <div className="deliverable-set__card" key={`dlv-${i}`}>
            <div className="deliverable-set__field">
              <label className="deliverable-set__label">成果物名（既存から選択）</label>
              <select
                className="db-select-control"
                value={isExisting ? (item.product || '') : ''}
                onChange={(e) => handleSelectProduct(i, e.target.value)}
              >
                <option value="">（既存から選択）</option>
                {selectOpts.filter(Boolean).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <label className="deliverable-set__label">または新規入力</label>
              <input
                className="db-input-control"
                value={isExisting ? '' : (item.product || '')}
                onChange={(e) => handleNewProduct(i, e.target.value)}
                placeholder="新しい成果物名を入力"
                disabled={isExisting}
              />
            </div>
            <div className="deliverable-set__field">
              <label className="deliverable-set__label">成果物URL</label>
              <input
                className="db-input-control"
                type="url"
                value={item.deliverable_url || ''}
                onChange={(e) => updateItem(i, { deliverable_url: e.target.value })}
                placeholder="https://..."
                readOnly={isExisting}
                title={isExisting ? '既存成果物のURLは成果物管理画面で編集してください' : undefined}
              />
            </div>
            <div className="deliverable-set__checks">
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(item.is_deliverable_stored)}
                  onChange={(e) => updateItem(i, { is_deliverable_stored: e.target.checked })}
                />
                成果物保管済み
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(item.pmo_deliverable_check)}
                  onChange={(e) => updateItem(i, { pmo_deliverable_check: e.target.checked })}
                />
                PMO成果物保管チェック
              </label>
              <button
                type="button"
                className="deliverable-set__remove"
                onClick={() => removeItem(i)}
                title="この成果物を削除"
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
      <div className="deliverable-set__actions">
        <button
          type="button"
          className="deliverable-set__add"
          onClick={addItem}
          disabled={hasNoDeliverableMark}
          title={hasNoDeliverableMark
            ? 'この領域は「成果物なし」なので成果物を登録できません'
            : undefined}
        >
          ＋ 成果物を追加
        </button>
        <button
          type="button"
          className="deliverable-set__none"
          onClick={toggleNoDeliverable}
          disabled={!hasNoDeliverableMark && hasRealDeliverable}
          title={!hasNoDeliverableMark && hasRealDeliverable
            ? 'この領域には成果物が登録されているため「成果物なし」にできません'
            : undefined}
        >
          {hasNoDeliverableMark ? '「成果物なし」を解除' : '成果物なし'}
        </button>
      </div>
    </div>
  );
}
